import type { CSSProperties, ReactNode } from 'react';
import type { NameFont } from '@spawnwire/shared';
// Self-hosted (the CSP allows fonts only from this origin). Each file holds @font-face rules;
// the browser downloads a font only once a name using it is on screen.
import '@fontsource/pixelify-sans/600.css';
import '@fontsource/press-start-2p/400.css';
import '@fontsource/caveat/700.css';
import '@fontsource/playfair-display/700.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/unifrakturcook/700.css';
import '@fontsource/orbitron/700.css';
import '@fontsource/creepster/400.css';
import '@fontsource/bungee/400.css';

/** Picker order and labels; `null` is the default font. */
export const NAME_FONT_OPTIONS: { value: NameFont | null; label: string }[] = [
  { value: null, label: 'Default' },
  { value: 'pixel', label: '8-Bit' },
  { value: 'arcade', label: 'Arcade' },
  { value: 'handwritten', label: 'Handwritten' },
  { value: 'elegant', label: 'Elegant' },
  { value: 'bubbly', label: 'Bubbly' },
  { value: 'gothic', label: 'Gothic' },
  { value: 'scifi', label: 'Sci-Fi' },
  { value: 'spooky', label: 'Spooky' },
  { value: 'chunky', label: 'Chunky' },
];

// Sizes are relative to the surrounding text so a styled name lines up with plain ones.
const STYLES: Record<NameFont, CSSProperties> = {
  pixel: { fontFamily: "'Pixelify Sans', monospace", fontWeight: 600, letterSpacing: '0.02em' },
  arcade: { fontFamily: "'Press Start 2P', monospace", fontWeight: 400, fontSize: '0.72em', letterSpacing: '0.02em' },
  handwritten: { fontFamily: "'Caveat', cursive", fontWeight: 700, fontSize: '1.18em' },
  elegant: { fontFamily: "'Playfair Display', serif", fontWeight: 700 },
  bubbly: { fontFamily: "'Fredoka', sans-serif", fontWeight: 600 },
  gothic: { fontFamily: "'UnifrakturCook', serif", fontWeight: 700, fontSize: '1.05em' },
  scifi: { fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: '0.9em', letterSpacing: '0.03em' },
  spooky: { fontFamily: "'Creepster', cursive", fontWeight: 400, fontSize: '1.08em', letterSpacing: '0.04em' },
  chunky: { fontFamily: "'Bungee', sans-serif", fontWeight: 400, fontSize: '0.88em' },
};

/** A display name in the person's chosen font (plain text when they use the default). */
export function NameText({ font, children }: { font: NameFont | null | undefined; children: ReactNode }) {
  if (!font || !STYLES[font]) return <>{children}</>;
  return <span style={STYLES[font]}>{children}</span>;
}
